import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CreateRestaurantInput, Restaurant, UpdateRestaurantInput } from '@/queries/useRestaurants'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

interface RestaurantFormProps {
  restaurant?: Restaurant | null
  onSubmit: (data: CreateRestaurantInput | UpdateRestaurantInput) => void | Promise<void>
  isLoading?: boolean
  error?: Error | null
  onCancel?: () => void
}

export default function RestaurantForm({
  restaurant,
  onSubmit,
  isLoading = false,
  error,
  onCancel,
}: RestaurantFormProps) {
  const { t } = useTranslation('admin')
  
  // Create schema with translated error messages
  const restaurantSchema = z.object({
    name: z.string().min(1, t('restaurants.form.nameRequired')),
    email: z
      .union([
        z.string().email(t('restaurants.form.invalidEmail')),
        z.literal(''),
      ])
      .optional(),
    phone: z.string().optional(),
  })

  type RestaurantFormData = z.infer<typeof restaurantSchema>

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RestaurantFormData>({
    resolver: zodResolver(restaurantSchema),
    defaultValues: {
      name: restaurant?.name || '',
      email: restaurant?.email || '',
      phone: restaurant?.phone || '',
    },
  })

  const handleFormSubmit = async (data: RestaurantFormData) => {
    // Convert empty string to undefined for optional fields
    const submitData: CreateRestaurantInput | UpdateRestaurantInput = {
      name: data.name,
      email: data.email && data.email.trim() !== '' ? data.email : undefined,
      phone: data.phone && data.phone.trim() !== '' ? data.phone : undefined,
    }
    await onSubmit(submitData)
  }

  return (
    <form 
      onSubmit={handleSubmit(handleFormSubmit)} 
      className="flex flex-col gap-[var(--spacing-md)]"
    >
      {error && (
        <Alert variant="destructive" data-testid="restaurant-form-error">
          <AlertDescription>
            {error.message || t('restaurants.form.errorOccurred')}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-[var(--spacing-sm)]">
        <Label htmlFor="name" data-testid="restaurant-form-name-label">
          {t('restaurants.form.name')} <span className="text-destructive">*</span>
        </Label>
        <Input
          id="name"
          {...register('name')}
          placeholder={t('restaurants.form.namePlaceholder')}
          data-testid="restaurant-form-name-input"
          className={errors.name ? 'border-destructive' : ''}
        />
        {errors.name && (
          <p
            className="text-[0.8125rem] text-[var(--error)] mt-[var(--space-1)]"
            data-testid="restaurant-form-name-error"
          >
            {errors.name.message}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-[var(--spacing-sm)]">
        <Label htmlFor="email" data-testid="restaurant-form-email-label">
          {t('restaurants.form.email')}
        </Label>
        <Input
          id="email"
          type="email"
          {...register('email')}
          placeholder={t('restaurants.form.emailPlaceholder')}
          data-testid="restaurant-form-email-input"
          className={errors.email ? 'border-destructive' : ''}
        />
        {errors.email && (
          <p
            className="text-[0.8125rem] text-[var(--error)] mt-[var(--space-1)]"
            data-testid="restaurant-form-email-error"
          >
            {errors.email.message}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-[var(--spacing-sm)]">
        <Label htmlFor="phone" data-testid="restaurant-form-phone-label">
          {t('restaurants.form.phone')}
        </Label>
        <Input
          id="phone"
          type="tel"
          {...register('phone')}
          placeholder={t('restaurants.form.phonePlaceholder')}
          data-testid="restaurant-form-phone-input"
          className={errors.phone ? 'border-destructive' : ''}
        />
        {errors.phone && (
          <p
            className="text-[0.8125rem] text-[var(--error)] mt-[var(--space-1)]"
            data-testid="restaurant-form-phone-error"
          >
            {errors.phone.message}
          </p>
        )}
      </div>

      <div className="flex justify-end gap-[var(--spacing-sm)] rtl:justify-start pt-[var(--spacing-md)]">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting || isLoading}
            data-testid="restaurant-form-cancel-button"
          >
            {t('restaurants.form.cancel')}
          </Button>
        )}
        <Button
          type="submit"
          disabled={isSubmitting || isLoading}
          data-testid="restaurant-form-submit-button"
        >
          {isSubmitting || isLoading
            ? t('restaurants.form.saving')
            : restaurant
            ? t('restaurants.form.update')
            : t('restaurants.form.create')}
        </Button>
      </div>
    </form>
  )
}

